import { z } from "zod";

export const healthResponseSchema = z.object({
  status: z.literal("ok"),
  db: z.literal("connected"),
  timestamp: z.string().datetime(),
  version: z.string().min(1),
});

export const pingRequestSchema = z.object({
  message: z
    .string()
    .min(1, "message is required")
    .max(200, "message must be 200 characters or fewer"),
});

export const pingResponseSchema = z.object({
  pong: z.literal(true),
  echo: z.string(),
});

export const apiErrorBodySchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
  }),
});

/* ── Celebrity opponents ── */

export const voicePhaseSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(100),
  // base64 data URL. D1 caps a single row at 2 MB, so per-clip cap keeps a
  // photo (imageDataUrl) plus a few phases under that ceiling. Clips are
  // re-encoded to mono WAV in the admin form (16 kHz under ~30s, 8 kHz above),
  // so ~1.4M chars covers ~60 seconds of speech.
  audioDataUrl: z.string().max(1_400_000),
  order: z.number().int().min(0),
});

export const celebrityInputSchema = z.object({
  name: z.string().min(1, "name is required").max(100, "name must be 100 characters or fewer"),
  // base64 data URL of the profile photo.
  imageDataUrl: z.string().max(500_000),
  difficulty: z.enum(["easy", "medium", "hard", "beast"]),
  secondsPerRep: z.number().min(0.5).max(120),
  voicePhases: z.array(voicePhaseSchema).max(10),
  // Round length in seconds; 0 = no time limit. Optional so existing clients
  // (and tests) keep working — missing values default to 0.
  workoutDurationSec: z.number().int().min(0).max(3600).default(0),
});

export const celebritySchema = celebrityInputSchema.extend({
  id: z.string().min(1),
});

export const celebrityListResponseSchema = z.array(celebritySchema);

/* ── Public video feed ── */

export const publicVideoMetaSchema = z.object({
  name: z
    .string()
    .min(1, "name is required")
    .max(100, "name must be 100 characters or fewer")
    .default("Pushup clip"),
  mimeType: z.string().min(1).max(50),
  durationSec: z.number().min(0).max(3600),
  reps: z.number().int().min(0).max(10_000),
  celebrityName: z.string().min(1).max(100).nullish(),
});

export const publicVideoSchema = publicVideoMetaSchema.extend({
  id: z.string().min(1),
  mediaUrl: z.string().min(1),
  createdAt: z.number().int().positive(),
  mine: z.boolean(),
});

export const publicVideoListResponseSchema = z.array(publicVideoSchema);

export type VoicePhase = z.infer<typeof voicePhaseSchema>;
export type CelebrityInput = z.infer<typeof celebrityInputSchema>;
export type Celebrity = z.infer<typeof celebritySchema>;
export type CelebrityListResponse = z.infer<typeof celebrityListResponseSchema>;

export type PublicVideoInput = z.infer<typeof publicVideoMetaSchema>;
export type PublicVideo = z.infer<typeof publicVideoSchema>;
export type PublicVideoListResponse = z.infer<typeof publicVideoListResponseSchema>;

export type HealthResponse = z.infer<typeof healthResponseSchema>;
export type PingRequest = z.infer<typeof pingRequestSchema>;
export type PingResponse = z.infer<typeof pingResponseSchema>;
export type ApiErrorBody = z.infer<typeof apiErrorBodySchema>;
